import $ from "jquery";

/* WF-1811: the drawer is opened and closed from more than one place now (burger tap, Escape), so the
   open/closed state is derived once here and broadcast as DRAWER_EVENT. fix_nav_scrolling.js
   listens for it instead of the raw burger click — otherwise closing via Escape would leave
   body.body-fix-scrolling behind and lock the page.
   Named ":state", not ":toggle": it reports what the drawer now is, it does not ask anyone to flip it. */
export const DRAWER_EVENT = "nav-drawer:state";

export default {
    defaultTitle: "Home",
    depth: 0,
    duration: 3000,
    dom: {},
    history: [],
    /* The overlay starts closed (Tailwind `hidden`). State is tracked explicitly rather than read back
       from the element, because slideDown/slideUp animate and :visible would lie mid-animation. */
    open: false,
    /* WF-1812: what this component made inert, so exactly those get released again. Two separate lists
       because they have different lifetimes: the background is marked per open (applyBackgroundInert),
       the parked columns per column change (updateColumnInert) and independently of open/closed — the
       overlay is display:none while closed, so nothing in it is in the accessibility tree either way. */
    inerted: [],
    columnInerted: [],

    init: function() {
        this.cacheDom();
        if (!this.dom.overlay.length) {
            this.armFlyout();
            return;
        }
        this.mount();
    },
    /* The page ships the bar only. The drawer arrives in #nav-flyout-slot. */
    armFlyout: function() {
        if (this._flyoutArmed) {
            return;
        }
        this._flyoutArmed = true;
        const arrive = () => {
            if (this._flyoutMounted) {
                return;
            }
            this.cacheDom();
            if (!this.dom.overlay.length) {
                return;
            }
            this._flyoutMounted = true;
            this.dom.toggle.off("click.navFlyout");
            this.markActiveLinks();
            this.mount();
        };
        document.addEventListener("nav-flyout:ready", arrive, { once: true });
        if (window.__navFlyout && typeof window.__navFlyout.then === "function") {
            window.__navFlyout.then(arrive);
        }
        if (this.dom.toggle.length) {
            this.dom.toggle.on("click.navFlyout", () => {
                /* __loadNavFlyout retries a failed request. Without it, or if the retry fails
                   too, the tap goes to the footer links instead of doing nothing. */
                const load = typeof window.__loadNavFlyout === "function"
                    ? window.__loadNavFlyout()
                    : window.__navFlyout || Promise.resolve();
                Promise.resolve(load).then(() => {
                    arrive();
                    if (this.dom.overlay.length) {
                        this.toggleDrawer();
                    } else {
                        this.fallbackToFooter();
                    }
                });
            });
        }
    },
    /* The drawer is rendered in its own request, where the navigation plugin cannot see the
       page URL. Mark the link of the current page the way navigationMobile.twig does. */
    markActiveLinks: function() {
        const current = window.location.pathname.replace(/\/+$/, "") || "/";
        $("#nav-overlay .menulink").each(function() {
            const href = this.getAttribute("href");
            if (!href) {
                return;
            }
            const path = new URL(href, window.location.origin).pathname.replace(/\/+$/, "") || "/";
            if (path === current) {
                $(this).addClass("active").removeClass("text-secondary-900 text-white!");
            }
        });
    },
    fallbackToFooter: function() {
        const footer = document.querySelector("footer");
        if (footer) {
            footer.scrollIntoView({ behavior: "smooth" });
        }
    },
    mount: function() {
        this.initDom();
        this.initEvents();
        this.initViewportGuard();
        this.checkDepth();
        this.updateFocusability();
    },
    cacheDom: function() {
        this.dom = {
            nav: $("#mainnav"),
            toggle: $("#toggle-nav"),
            overlay: $("#nav-overlay"),
            container: $("#drawer-container"),
            drawer: $("#drawer-content"),
            /* Sits in the nav bar, i.e. outside the overlay and reachable while the drawer is open. */
            cta: $("#nav-cta-mobile"),
            /* Only #nav-overlay itself carries .hide-overlay since the #close button was dropped.
               #drawer-container covers the overlay and stops propagation, so this is a safety net for
               clicks on uncovered overlay area rather than a control anyone can reach today. */
            close: $(".hide-overlay"),
            prev: $("#prev"),
            title: $("#title"),
        };
    },
    checkDepth: function() {
        if(this.depth === 0) {
            this.dom.prev.hide();
        }else {
            this.dom.prev.show();
        }
        if (this.open) {
            /* Runs after every slideMenu: the visible column changed and #prev just appeared or
               disappeared, so the trap's cycle has to be rebuilt — after this, not inside slideMenu. */
            this.rebuildFocusCycle();
        }
    },
    initDom: function() {
        this.parent = this.dom.drawer.find("menu").first();
        this.dom.title.html(this.defaultTitle);
    },

    initEvents: function() {
        /* Only stops propagation so a click inside the drawer does not reach #nav-overlay's close
           handler. It used to `return false`, which jQuery turns into preventDefault + stopPropagation
           — that cancelled the default action of every bubbled click, which is why drawer links needed
           a `window.location` workaround. Cancelling defaults broke Cmd/Ctrl-click, Shift-click and
           target="_blank" on nav links; since WF-1811 made the drawer keyboard-operable, Cmd/Ctrl-Enter
           on a focused link is a reachable path too. */
        this.dom.container.on("click", (e) => {
            e.stopPropagation();
        });
        this.dom.toggle.on("click", this.toggleDrawer.bind(this));
        this.dom.close.on("click", this.closeDrawer.bind(this));

        /* WF-1812: the nav bar stays operable while the drawer is open (that is what keeps the burger
           tappable), so the CTA next to it can be pressed with the drawer still up. On pages with a
           Calendly popup the CTA opens an overlay of its own (onclick="showCalendly()", see
           navigationMobile.twig) — into a document whose background this component just made inert and
           whose Tab order is still trapped inside nav bar + drawer. Handing the page over by closing
           first is the whole fix; without it the Calendly form would be unreachable by keyboard.
           Focus deliberately stays on the CTA the user just pressed instead of jumping to the burger:
           Calendly does not move focus into its own widget, and the burger sits behind that overlay. */
        this.dom.cta.on("click", () => {
            this.closeDrawer({ returnFocus: false });
        });

        /* Namespaced and re-bound, so a second init() does not stack another handler. */
        $(document).off("keydown.customNavigation").on("keydown.customNavigation", (e) => {
            if (!this.open) {
                return;
            }
            if (e.key === "Escape") {
                this.closeDrawer();
            } else if (e.key === "Tab") {
                this.trapTab(e);
            }
        });

        /* Only the submenu openers need intercepting. Real links are left alone and navigate natively,
           which restores target="_blank", modifier-clicks and middle-click — the `window.location`
           assignment that used to stand here honoured none of them. */
        this.dom.drawer.find("button.parent").on("click",
            function(e) {
                this.slideMenu($(e.currentTarget).closest("li"));
                this.checkDepth();
            }.bind(this)
        );

        this.dom.prev.on("click",
            function() {
                if (this.depth) {
                    this.slideMenu(null);
                    this.checkDepth();
                }
            }.bind(this)
        );
    },

    hasChildren: function(target) {
        return target.hasClass("parent");
    },

    isOpen: function() {
        return this.open;
    },

    toggleDrawer: function() {
        if (this.open) {
            this.closeDrawer();
        } else {
            this.openDrawer();
        }
    },

    openDrawer: function() {
        if (this.open) {
            return;
        }
        this.dom.overlay.slideDown("slide");
        this.applyBackgroundInert();
        this.publishState(true);
        this.rebuildFocusCycle();
        /* The drawer is position:fixed and rendered late in the document (part 'flyout'), so DOM order
           does not lead into it — focus has to be moved explicitly. Taking the first cycle entry inside
           the overlay reuses the visibility rules, so reopening at depth > 0 lands on #prev.
           Falls back to the burger rather than making the overlay focusable: the burger is visible above
           the overlay, is named, closes the drawer, and is already part of the cycle — so focus always
           ends up somewhere the trap can work with, even if the drawer renders without controls. */
        const overlay = this.dom.overlay[0];
        const firstInDrawer = this.focusCycle.filter((el) => overlay.contains(el))[0];
        const target = firstInDrawer || this.dom.toggle[0];
        if (target) {
            target.focus();
        }
    },

    /* `options` is optional. Some callers are bound directly as jQuery handlers and therefore pass an
       event object in its place — that has no returnFocus, so the default (return focus to the burger)
       applies. Only the nav-bar CTA opts out, see initEvents(). */
    closeDrawer: function(options) {
        if (!this.open) {
            return;
        }
        const returnFocus = !(options && options.returnFocus === false);
        this.dom.overlay.slideUp("slide");
        this.releaseBackgroundInert();
        this.publishState(false);
        if (returnFocus) {
            this.dom.toggle.trigger("focus");
        }
    },

    /* Kept as the pre-WF-1811 entry point: a single call that flips the drawer. */
    slideDrawer: function() {
        this.toggleDrawer();
    },

    publishState: function(open) {
        this.open = open;
        this.dom.toggle.attr("aria-expanded", open ? "true" : "false");
        document.dispatchEvent(
            new CustomEvent(DRAWER_EVENT, { detail: { open: open } })
        );
    },

    /* WF-1812. The overlay looks like a modal dialog but the page it covers stays in the accessibility
       tree, so a screen reader's virtual cursor reads straight on from the last drawer entry into the
       ~60 hidden controls behind it, with nothing signalling that the menu was left. `inert` takes a
       subtree out of the accessibility tree and the tab order in one step, which is exactly the gap the
       WF-1811 focus trap does not close: that one only governs Tab, and the virtual cursor does not
       follow Tab.
       Two subtrees stay live: the overlay and #mainnav — the same boundary rebuildFocusCycle() already
       draws. The nav bar starts above the overlay (top-16) and stays visible, and since WF-1811 dropped
       #close the burger is the ONLY visible way to close, so inerting the bar would not just be
       redundant, it would break tap-to-close.
       Neither of the two is a direct child of <body> (both sit inside #appWrapper > #appContent), so
       inerting body's children would inert nothing that is actually covered. Walk up from both instead
       and inert what hangs off the path on every level; the path itself stays live.
       No aria-modal="true" (and hence no role="dialog") on the overlay: it tells screen readers to
       ignore everything outside the dialog — which is where the burger lives. That would hide the only
       visible close control, the exact opposite of the intent here. */
    SKIP_INERT: ["SCRIPT", "STYLE", "LINK", "TEMPLATE"],

    /* Ancestor chain of `el`, excluding `stop`, added into `path`. */
    collectPath: function(el, stop, path) {
        for (let node = el; node && node !== stop; node = node.parentElement) {
            path.add(node);
        }
        return path;
    },

    /* Inerts everything that hangs off `path` on every level and appends what it marked to `marked`.
       Both boundaries in this component are of the shape "everything except this one chain stays
       reachable", so they share this. Elements that already carry inert are left alone and are not
       collected, so a mark another script owns is never taken over and never handed back. */
    markInertOffPath: function(path, marked) {
        path.forEach((node) => {
            const parent = node.parentElement;
            if (!parent) {
                return;
            }
            Array.from(parent.children).forEach((sibling) => {
                if (path.has(sibling) || this.SKIP_INERT.includes(sibling.tagName)) {
                    return;
                }
                if (sibling.hasAttribute("inert")) {
                    return;
                }
                sibling.setAttribute("inert", "");
                marked.push(sibling);
            });
        });
        return marked;
    },

    applyBackgroundInert: function() {
        const live = [this.dom.overlay[0], this.dom.nav[0]].filter(Boolean);
        if (!live.length || !document.body) {
            return;
        }

        const path = new Set();
        live.forEach((el) => this.collectPath(el, document.body, path));

        /* Only what exists right now is marked, and only what was marked is released again on close.
           That is what keeps overlays injected while the drawer is open operable — a Calendly modal, a
           Cookiebot banner, a Sentry widget: they arrive as new children and are never touched. */
        this.inerted = this.markInertOffPath(path, []);
    },

    releaseBackgroundInert: function() {
        this.inerted.forEach((el) => {
            el.removeAttribute("inert");
        });
        this.inerted = [];
    },

    /* The drawer and the burger both live in a `xl:hidden` wrapper: crossing the xl breakpoint with the
       drawer open (a tablet rotating from 1024 to 1366 does exactly that) hides the overlay AND the
       burger, and with an inert background that would leave a page with nothing operable on it at all.
       Close on the way up so the inert marks are released with it. */
    DESKTOP_QUERY: "(min-width: 1280px)",

    initViewportGuard: function() {
        if (typeof window.matchMedia !== "function") {
            return;
        }
        /* init() can run more than once (e.g. re-init after markup swaps), so drop the previous
           listener rather than stacking another one on a fresh MediaQueryList. */
        if (this.viewportQuery && this.viewportListener) {
            if (this.viewportQuery.removeEventListener) {
                this.viewportQuery.removeEventListener("change", this.viewportListener);
            } else if (this.viewportQuery.removeListener) {
                this.viewportQuery.removeListener(this.viewportListener);
            }
        }

        this.viewportQuery = window.matchMedia(this.DESKTOP_QUERY);
        this.viewportListener = (e) => {
            if (e.matches) {
                this.closeDrawer();
            }
        };

        if (this.viewportQuery.addEventListener) {
            this.viewportQuery.addEventListener("change", this.viewportListener);
        } else if (this.viewportQuery.addListener) {
            /* Safari < 14 */
            this.viewportQuery.addListener(this.viewportListener);
        }
    },

    /* WF-1811 focus trap. The overlay is opaque and covers everything below the nav bar, but it is
       rendered late in the document (after the footer, see WF-1642), so Tab would walk straight out of
       it into ~60 focusable elements the user cannot see. Cycle over what is actually on screen
       instead: the nav bar stays on top of the overlay, so it stays reachable — that also keeps the
       burger itself tabbable as a second way to close. Escape is the guaranteed way out, without which
       trapping Tab would itself breach WCAG 2.1.2 No Keyboard Trap. */
    FOCUSABLE_SELECTOR: "a[href], button, input, select, textarea, [tabindex]",

    /* Built once per open (and per column change) rather than per keystroke: it reads computed styles,
       and Tab is on the interaction path we watch for INP. */
    rebuildFocusCycle: function() {
        const cycle = [];

        [this.dom.nav, this.dom.overlay].forEach((scope) => {
            if (!scope.length) {
                return;
            }
            const root = scope[0];
            scope.find(this.FOCUSABLE_SELECTOR).each((i, el) => {
                if (el.disabled || el.getAttribute("tabindex") === "-1") {
                    return;
                }
                if (this.isVisibleWithin(el, root)) {
                    cycle.push(el);
                }
            });
        });

        this.focusCycle = cycle;
    },

    /* Walks up to (not including) root, which is visible by definition whenever this runs. Catches
       both the Tailwind-hidden desktop nav and the elements slideMenu/checkDepth hide inline. */
    isVisibleWithin: function(el, root) {
        for (let node = el; node && node !== root; node = node.parentElement) {
            const style = window.getComputedStyle(node);
            if (style.display === "none" || style.visibility === "hidden") {
                return false;
            }
        }
        return true;
    },

    trapTab: function(e) {
        const cycle = this.focusCycle || [];
        if (cycle.length < 2) {
            return;
        }

        const index = cycle.indexOf(document.activeElement);
        const first = cycle[0];
        const last = cycle[cycle.length - 1];

        if (index === -1) {
            /* Focus sits outside the cycle — e.g. it was returned from the browser UI to the body.
               Pull it back to the edge the user is tabbing towards. */
            e.preventDefault();
            (e.shiftKey ? last : first).focus();
        } else if (!e.shiftKey && index === cycle.length - 1) {
            e.preventDefault();
            first.focus();
        } else if (e.shiftKey && index === 0) {
            e.preventDefault();
            last.focus();
        }
    },

    /* Sub-menus are not hidden but parked off-canvas (left: 100%), so without this every link of
       every nested level would sit in the tab order of the visible column. Only the links of the
       column currently on screen stay focusable. */
    updateFocusability: function() {
        /* depth and history are kept in step by slideMenu, but only read the history when it actually
           has an entry — a mismatch should degrade to the root column, not throw. */
        const openLi = this.depth && this.history.length
            ? this.history[this.history.length - 1]
            : null;
        const active = openLi ? openLi.children("menu").first() : this.parent;

        if (!active || !active.length) {
            return;
        }

        this.dom.drawer.find("a, button.parent").each(function() {
            const inActiveColumn = $(this).closest("menu")[0] === active[0];
            this.setAttribute("tabindex", inActiveColumn ? "0" : "-1");
        });

        this.updateColumnInert(active);
    },

    /* WF-1812, second half. tabindex="-1" above keeps the parked columns out of the tab order, but the
       virtual cursor follows the accessibility tree, so all of them were still read: measured on the
       home page at depth 0, 12 controls on screen against 83 parked off-canvas ones — and interleaved,
       not appended, because each column sits inside the <li> that opens it. Swiping past the first entry
       meant swiping through the whole tree before reaching the second visible one.
       Same "one live chain" shape as applyBackgroundInert, in two parts, because the columns are nested
       rather than siblings: a parent column CONTAINS its child, so it cannot simply be inerted itself.
         - above the active column: inert what hangs off the path — the other <li>s and, inside the <li>
           that was opened, its own link and opener, which the slide moved off-canvas to the left.
         - below it: inert the columns parked inside the active one (left: 100%). Only the nearest ones,
           the deeper levels inherit it.
       Runs on every column change, so previous marks are released first: the column that slides in has
       to lose its inert before slideMenu moves focus into it. */
    updateColumnInert: function(active) {
        this.columnInerted.forEach((el) => {
            el.removeAttribute("inert");
        });

        const path = this.collectPath(active[0], this.dom.drawer[0], new Set());
        const marked = this.markInertOffPath(path, []);

        active
            .children("li")
            .children("menu")
            .each(function() {
                if (!this.hasAttribute("inert")) {
                    this.setAttribute("inert", "");
                    marked.push(this);
                }
            });

        this.columnInerted = marked;
    },

    slideMenu: function(target) {
        let title = this.defaultTitle;
        /* The column slides sideways instead of appearing below its opener, so the opener ends up
           off-canvas (measured: left: -76px) and untabbable. Leaving focus on it means the next Tab
           finds nothing in the cycle and jumps back to the top of the nav bar. Focus has to follow the
           column: into the new one going forward, back onto the opener when returning. */
        let focusTarget = null;
        if (target) {
            this.depth++;
            this.history.push(target);
            target.siblings().hide();
            target.show();

            title = target.find("> a:first-child").html();
            focusTarget = target
                .children("menu")
                .first()
                .find(this.FOCUSABLE_SELECTOR)
                .first();
        } else {
            this.depth--;
            target = this.history.pop();
            target.siblings().show();
            focusTarget = target.children("button.parent").first();

            if (this.depth) {
                title = target
                    .closest("menu")
                    .closest("li")
                    .find("> a:first-child")
                    .html();
            }
        }

        this.parent.css({
            left: -this.depth * 100 + "%",
        });
        this.dom.title.html(title);
        this.dom.prev.toggleClass("visible", !!this.depth);
        this.updateFocusability();

        if (this.open && focusTarget && focusTarget.length) {
            focusTarget.trigger("focus");
        }
    },
};
